import {
createContext,
useCallback,
useContext,
useEffect,
useMemo,
useState,
} from "react";
import type { ReactNode } from "react";
import {
detectPlatform,
getInstallState,
isRunningStandalone,
markDismissed,
type BeforeInstallPromptEvent,
type InstallState,
} from "./pwa";
interface PwaContextValue {
state: InstallState;
modalOpen: boolean;
openInstall: () => Promise<void>;
dismissInstall: () => void;
closeModal: () => void;
}
const PwaContext = createContext<PwaContextValue | null>(null);
export function PwaProvider({ children }: { children: ReactNode }) {
const [deferredPrompt, setDeferredPrompt] =
useState<BeforeInstallPromptEvent | null>(null);
const [modalOpen, setModalOpen] = useState(false);
const [refreshTick, setRefreshTick] = useState(0);
useEffect(() => {
const handler = (e: Event) => {
e.preventDefault();
setDeferredPrompt(e as BeforeInstallPromptEvent);
};
window.addEventListener("beforeinstallprompt", handler);
const installedHandler = () => {
setDeferredPrompt(null);
setModalOpen(false);
setRefreshTick((t) => t + 1);
};
window.addEventListener("appinstalled", installedHandler);
return () => {
window.removeEventListener("beforeinstallprompt", handler);
window.removeEventListener("appinstalled", installedHandler);
};
}, []);
const state = useMemo(() => {
void refreshTick;
return getInstallState(deferredPrompt);
}, [deferredPrompt, refreshTick]);
const openInstall = useCallback(async () => {
if (state.isInstalled) return;
if (deferredPrompt) {
try {
await deferredPrompt.prompt();
const { outcome } = await deferredPrompt.userChoice;
if (outcome === "accepted") {
setDeferredPrompt(null);
} else {
markDismissed();
}
} catch {
setModalOpen(true);
}
return;
}
setModalOpen(true);
}, [deferredPrompt, state.isInstalled]);
const dismissInstall = useCallback(() => {
setModalOpen(false);
markDismissed();
}, []);
const closeModal = useCallback(() => {
setModalOpen(false);
}, []);
const value = useMemo<PwaContextValue>(
() => ({ state, modalOpen, openInstall, dismissInstall, closeModal }),
[state, modalOpen, openInstall, dismissInstall, closeModal],
);
return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
}
export function usePwaInstall(): PwaContextValue {
const ctx = useContext(PwaContext);
if (!ctx) {
const platform = detectPlatform();
const installed = isRunningStandalone();
return {
state: {
platform,
isInstalled: installed,
canPromptNatively: false,
needsManualInstructions: false,
},
modalOpen: false,
openInstall: async () => {},
dismissInstall: () => {},
closeModal: () => {},
};
}
return ctx;
}
